'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceOption {
  id: string;
  name: string;
  icon: string;
}

interface ServiceSelectorProps {
  cargoTypes: ServiceOption[];
  destinations: ServiceOption[];
  onSelectionChange?: (cargo: string, destination: string) => void;
}

const ServiceSelector = ({ cargoTypes, destinations, onSelectionChange }: ServiceSelectorProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState<string>('');
  const [selectedDestination, setSelectedDestination] = useState<string>('');
  const [recommendation, setRecommendation] = useState<string>('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated && selectedCargo && selectedDestination) {
      generateRecommendation();
    }
  }, [selectedCargo, selectedDestination, isHydrated]);

  const generateRecommendation = () => {
    const recommendations: Record<string, string> = {
      'minerals-drc': 'Minerals Transport with enhanced security protocols for DRC operations',
      'minerals-kenya': 'Minerals Transport with efficient cross-border customs clearance',
      'equipment-drc': 'Heavy Equipment Logistics with specialized route surveys',
      'equipment-kenya': 'Heavy Equipment Logistics with port-to-site coordination',
      'personnel-drc': 'Personnel Movement with comprehensive safety measures',
      'personnel-kenya': 'Personnel Movement with reliable scheduling',
      'general-drc': 'Customs Brokerage with full compliance support',
      'general-kenya': 'Customs Brokerage with expedited processing',
    };

    const key = `${selectedCargo}-${selectedDestination}`;
    setRecommendation(recommendations[key] || 'Contact us for a customized logistics solution');
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
                Find Your Perfect Service
              </h2>
              <p className="font-body text-lg text-muted-foreground">
                Select your cargo type and destination for personalized recommendations
              </p>
            </div>
            <div className="bg-card rounded-2xl shadow-lg p-8 animate-pulse">
              <div className="h-64 bg-muted rounded-lg" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
              Find Your Perfect Service
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Select your cargo type and destination for personalized recommendations
            </p>
          </div>
          
          <div className="bg-card rounded-2xl shadow-lg p-8 border border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block font-headline font-semibold text-foreground mb-4">
                  Cargo Type
                </label>
                <div className="space-y-3">
                  {cargoTypes.map((cargo) => (
                    <button
                      key={cargo.id}
                      onClick={() => setSelectedCargo(cargo.id)}
                      className={`w-full flex items-center space-x-3 p-4 rounded-xl border-2 transition-all duration-300 ${
                        selectedCargo === cargo.id
                          ? 'border-golden-mid bg-gradient-to-r from-golden-light/10 to-golden-dark/10 shadow-golden-md'
                          : 'border-border hover:border-golden-light hover:bg-muted'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedCargo === cargo.id
                          ? 'bg-gradient-to-br from-golden-light to-golden-dark' :'bg-muted'
                      }`}>
                        <Icon
                          name={cargo.icon as any}
                          size={20}
                          className={selectedCargo === cargo.id ? 'text-primary' : 'text-muted-foreground'}
                        />
                      </div>
                      <span className={`font-body font-semibold ${
                        selectedCargo === cargo.id ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {cargo.name}
                      </span>
                      {selectedCargo === cargo.id && (
                        <Icon name="CheckCircleIcon" size={20} className="text-golden-mid ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block font-headline font-semibold text-foreground mb-4">
                  Destination Region
                </label>
                <div className="space-y-3">
                  {destinations.map((destination) => (
                    <button
                      key={destination.id}
                      onClick={() => setSelectedDestination(destination.id)}
                      className={`w-full flex items-center space-x-3 p-4 rounded-xl border-2 transition-all duration-300 ${
                        selectedDestination === destination.id
                          ? 'border-golden-mid bg-gradient-to-r from-golden-light/10 to-golden-dark/10 shadow-golden-md'
                          : 'border-border hover:border-golden-light hover:bg-muted'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedDestination === destination.id
                          ? 'bg-gradient-to-br from-golden-light to-golden-dark' :'bg-muted'
                      }`}>
                        <Icon
                          name={destination.icon as any}
                          size={20}
                          className={selectedDestination === destination.id ? 'text-primary' : 'text-muted-foreground'}
                        />
                      </div>
                      <span className={`font-body font-semibold ${
                        selectedDestination === destination.id ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {destination.name}
                      </span>
                      {selectedDestination === destination.id && (
                        <Icon name="CheckCircleIcon" size={20} className="text-golden-mid ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {recommendation && (
              <div className="bg-gradient-to-r from-golden-light/10 via-golden-mid/10 to-golden-dark/10 rounded-xl p-6 border-2 border-golden-mid">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-golden-light to-golden-dark flex items-center justify-center">
                    <Icon name="LightBulbIcon" size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-headline font-bold text-lg text-foreground mb-2">
                      Recommended Service
                    </h3>
                    <p className="font-body text-muted-foreground mb-4">
                      {recommendation}
                    </p>
                    <button className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-golden-light to-golden-dark rounded-lg font-cta font-semibold text-primary shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-0.5">
                      <span>Request Quote</span>
                      <Icon name="ArrowRightIcon" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSelector;